import { getOfficeDetailsUrl } from '@shared-components-web/components/GoogleMap/GoogleMap.helpers';
import { BrandThemeConfig, Maybe, TOfficeInfo } from '@shared-components-web/types';
import { getCleanPhoneNumberString } from '@shared-components-web/utils/resolveButtonEntryLink/resolveButtonEntryLink';

type FacilityFieldsFragment = any;

type OfficeInfoType = any;

export const ContentReplacementKeys: string[] = [
	'[phone]',
	'[address]',
	'[city]',
	'[state]',
	'[zip]',
	'[facilityCode]',
	'[reserve-url]',
	'[url]',
	'[order-online-url]',
	'[request-refill-url]',
	'[email]',
];

type TContentVar = { [K in (typeof ContentReplacementKeys)[number]]: string | undefined | null };

export const foundVarInPath = (path: string): boolean => {
	return ContentReplacementKeys.some((key) => path.includes(key));
};

export const getHasOfficeInfo = (
	officeInfo?: FacilityFieldsFragment | null,
	officeLocationInfo?: TOfficeInfo | null
): boolean => {
	return !!(officeInfo || (officeLocationInfo && Object.keys(officeLocationInfo).length > 0));
};

export const replaceContentVars = ({
	text,
	officeInfo,
	officeLocationInfo,
	config,
	isHref = false,
}: {
	text?: Maybe<string>;
	officeInfo: OfficeInfoType | null;
	officeLocationInfo: TOfficeInfo | null;
	config: BrandThemeConfig;
	isHref?: boolean;
}) => {
	if (text && officeInfo) {
		let replacedText = text;
		const hasOfficeInfo = getHasOfficeInfo(officeInfo, officeLocationInfo);

		if (!hasOfficeInfo) {
			return replacedText;
		}

		const address = {
			stateCode: officeInfo?.address?.stateCode ?? officeLocationInfo?.address?.state ?? '',
			city: officeInfo?.address?.city ?? officeLocationInfo?.address?.city ?? '',
			address1: officeInfo?.address?.address1 ?? officeLocationInfo?.address?.address1 ?? '',
			zipCode: officeInfo?.address.zipCode ?? officeLocationInfo?.address?.zipCode ?? '',
		};
		const facilityBasePath = config?.features?.otherApps?.facilityDetailsBasePath;
		const officeURL =
			getOfficeDetailsUrl({
				facilityBasePath,
				themeId: config.themeId,
				address,
			}) ?? '';
		const attributes = officeInfo?.attributes ?? officeLocationInfo?.attributes ?? [];
		const replacements: TContentVar = {
			'[phone]': officeInfo?.technicalInfrastructure?.phoneNumber ?? officeLocationInfo?.phoneNumber ?? '',
			'[address]': address.address1,
			'[city]': address.city,
			'[state]': address.stateCode,
			'[zip]': address.zipCode,
			'[facilityCode]': officeInfo.code ?? officeLocationInfo?.code ?? '',
			'[reserve-url]': officeInfo.scheduling?.schedulingUrl ?? '',
			'[url]': officeURL,
			'[order-online-url]':
				attributes.find((attribute) => attribute?.name?.toUpperCase() === 'ORDER ONLINE URL')?.value ?? '',
			'[request-refill-url]':
				attributes.find((attribute) => attribute?.name?.toUpperCase() === 'REQUEST REFILL URL')?.value ?? '',
			'[email]': officeInfo.email ?? '',
		};

		Object.keys(replacements).forEach((key) => {
			const availableValue = replacements[key];

			if (availableValue) {
				// prevents malformed phone numbers in tel: links:
				if (isHref && key === '[phone]' && !availableValue.startsWith('tel:')) {
					replacedText = replacedText.replace(key, `tel:${getCleanPhoneNumberString(availableValue)}`);
				} else if (isHref && key === '[email]' && !availableValue.startsWith('mailto:')) {
					replacedText = replacedText.replace(key, `mailto:${availableValue}`);
				}
				replacedText = replacedText.replace(key, availableValue);
			}
		});
		return replacedText;
	}
	return text ?? '';
};
